import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import TextFieldWithLabel from '@ui-parts/TextFieldWithLabel';
import Alert from '@ui-parts/Alert';
import InputValidationErrorBox from '@ui-parts/InputValidationErrorBox';
import Checkbox from '@ui-elements/Checkbox';
import Button from '@ui-elements/Button';
import RequiredLabel from '@ui-elements/RequiredLabel';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { FONT_SIZES, PALETTE, BASE_FONT_COLOR } from '~/assets/styles/base';
import { SURVEY_STATUSES } from '~/constants';
import { ROUTES } from '~/constants/routes';
import { utf8mb4ValidationRule } from '~/constants/validationRules';
import { callThunk, fetchSurveys } from '~/api';

const Wrapper = styled.div`
  width: 716px;
  margin: 0 auto;
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.border.frame};
  margin-top: 94px;
  padding: 53px 68px;
`;

const TitleWrapper = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const Title = styled(Typography)`
  color: ${PALETTE.text.primary};
  margin-bottom: 34px;
`;

const Notes = styled.div`
  margin: 0 auto;
  border: solid #d4ddde;
  border-width: 1px 0 1px;
  padding: 13px;
`;

const NotesList = styled.ul`
  color: #0f3656;
  font-size: ${FONT_SIZES.body3};
  margin-top: 14px;
`;

const NotesListItem = styled.li`
  position: relative;
  padding: 0 0 0 17px;
  margin-top: 10px;
  line-height: 1.4;
  &:before {
    position: absolute;
    content: '・';
    left: 0;
    top: 0;
  }
`;

const StyledForm = styled.form`
  // HACK: 実値40pxだが、理由未選択エラー時にscrollIntoView際の追従ヘッダーpx分アジャスト
  padding-top: 70px;
  margin-top: -30px;
`;

const ReasonsTitle = styled.div`
  font-size: ${FONT_SIZES.title3};
  margin-bottom: 18px;
`;

const OtherReasonTextField = styled(TextField)`
  width: 400px;
  // Chromeのオートコンプリートinput背景色を消す
  input:-webkit-autofill {
    box-shadow: 0 0 0 1000px white inset;
  }
  .MuiOutlinedInput-root {
    padding: 14px;
    font-size: ${FONT_SIZES.body3};
    color: ${PALETTE.text.main};
    background-color: ${PALETTE.white};
    &.Mui-disabled {
      background: #f4f4f4;
    }
  }
`;

const Opinion = styled.div`
  margin-top: 4px;
  .MuiBox-root {
    padding: 0;
  }
  .MuiTypography-root {
    font-size: ${FONT_SIZES.body3};
    color: ${BASE_FONT_COLOR};
  }
  .MuiOutlinedInput-input {
    padding: 0;
    font-size: ${FONT_SIZES.body3};
    color: ${BASE_FONT_COLOR};
  }
`;

const SubmitButton = styled(Button)`
  display: block;
  width: 148px;
  height: 40px;
  margin: 32px auto 0;
`;

const InquiryButton = styled(Button)`
  display: block;
  border: 1px solid #e26666;
  margin: 16px auto 0;
  min-width: 84px;
  color: ${PALETTE.error.marginTop};
`;

const StyledIcon = styled(WarningRoundedIcon)`
  font-size: 2.8rem;
  margin-right: 6px;
`;

const NoRequiredGmoResearchInfo = styled.div`
  margin-top: 30px;
`;

function Withdraw() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const initialReasons = [
    { text: t('withdraw.form.reasons.1'), checked: false },
    { text: t('withdraw.form.reasons.2'), checked: false },
    { text: t('withdraw.form.reasons.3'), checked: false },
    { text: t('withdraw.form.reasons.4'), checked: false },
    { text: t('withdraw.form.reasons.5'), checked: false },
    { text: t('withdraw.form.reasons.6'), checked: false },
    { text: t('withdraw.form.reasons.7'), checked: false },
  ];
  const initialOtherReason = { text: t('withdraw.form.otherReason'), checked: false, value: '' };

  const [reasons, setReasons] = useState(initialReasons);
  const [otherReason, setOtherReason] = useState(initialOtherReason);
  const [opinion, setOpinion] = useState('');
  // 退会可能判定の中、初期値のnullの間は表示コンテンツはなしとする
  const [disabledWithdraw, setDisabledWithdraw] = useState(null);
  // 退会後のお知らせ希望有無 API送信時はreasonsに含めるが、表示位置が別なのとreasonsのバリデーションには含めないため、独立したstateとして保持
  const [noRequiredGmoResearchInfo, setNoRequiredGmoResearchInfo] = useState({
    text: t('withdraw.form.noRequiredGmoResearchInfo'),
    checked: false,
  });
  const formRef = useRef(null);

  const disabledWithdrawSurveyStatusIds = [
    SURVEY_STATUSES.UNDER_REVIEW,
    SURVEY_STATUSES.APPROVED,
    SURVEY_STATUSES.COLLECTING,
  ];

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      otherReason: '',
      opinion: '',
    },
  });

  // 初期表示、Surveysを取得して退会可否を判定
  useEffect(() => {
    (async () => {
      const {
        payload: { result },
      } = await dispatch(
        callThunk({
          action: fetchSurveys,
          payload: {
            surveyStatusIds: disabledWithdrawSurveyStatusIds.join(','),
          },
        })
      );
      setDisabledWithdraw(result?.length !== 0);
    })();

    // 確認画面の戻るボタンから来たときは画面を復元する
    if (location?.state?.referrer === ROUTES.USER.WITHDRAW_CONFIRM) {
      setReasons(location.state.reasons);
      setOtherReason(location.state.otherReason);
      setNoRequiredGmoResearchInfo(location.state.noRequiredGmoResearchInfo);
      setOpinion(location.state.opinion);
      setValue('otherReason', location.state.otherReason.value);
      setValue('opinion', location.state.opinion);
    }
  }, []);

  useEffect(() => {
    if (errors?.reasons) {
      formRef?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [errors?.reasons]);

  const onSubmit = () => {
    navigate(ROUTES.USER.WITHDRAW_CONFIRM, {
      state: {
        reasons,
        otherReason,
        noRequiredGmoResearchInfo,
        opinion,
        referrer: window.location.pathname,
      },
    });
  };

  return (
    <>
      {disabledWithdraw && (
        <Alert
          marginTop="100px"
          head={
            <>
              <StyledIcon />
              {t('withdraw.disabledWithdraw.head')}
            </>
          }
          body={
            <>
              {t('withdraw.disabledWithdraw.body')}
              <InquiryButton
                color="error"
                size="small"
                handleClick={() => window.open(ROUTES.COMMON.WITHDRAW_INQUIRY, '_blank')}
              >
                {t('withdraw.disabledWithdraw.button')}
              </InquiryButton>
            </>
          }
        />
      )}

      {disabledWithdraw === false && (
        <Wrapper>
          <TitleWrapper>
            <Title variant="title1" component="p">
              {t('withdraw.form.title')}
            </Title>
            <Typography variant="bodyDefault">{t('withdraw.form.caution')}</Typography>
          </TitleWrapper>
          <Notes>
            <Typography variant="body3" fontWeight="medium">
              {t('withdraw.form.notesTitle')}
            </Typography>
            <NotesList>
              <NotesListItem>{t('withdraw.form.notesItem1')}</NotesListItem>
              <NotesListItem>{t('withdraw.form.notesItem2')}</NotesListItem>
              <NotesListItem> {t('withdraw.form.notesItem3')}</NotesListItem>
            </NotesList>
          </Notes>

          <StyledForm
            onSubmit={handleSubmit(onSubmit)}
            ref={formRef}
            data-testid="withdraw-inputsForm"
          >
            <ReasonsTitle>
              <RequiredLabel>{t('validation.requiredLabel')}</RequiredLabel>
              {t('withdraw.form.reasonsTitle')}
            </ReasonsTitle>
            {!!errors?.reasons && (
              <InputValidationErrorBox
                message={errors.reasons.message}
                dataTestId="reasons"
                position="left"
              />
            )}
            {reasons.map((item, i) => {
              return (
                <Checkbox
                  key={item.text}
                  name="reasons"
                  label={item.text}
                  checked={item.checked}
                  handleChange={(e) => {
                    const res = [...reasons];
                    res[i] = { text: item.text, checked: e.target.checked };
                    setReasons(res);
                  }}
                  dataTestId={`Withdraw-ReasonCheckbox-${i}`}
                  inputProps={{
                    ...register('reasons', {
                      validate: {
                        message: () =>
                          reasons.every((value) => !value.checked) && !otherReason.checked
                            ? t('validation.requiredChoice')
                            : null,
                      },
                    }),
                  }}
                />
              );
            })}
            <Checkbox
              name="reasons"
              label={otherReason.text}
              checked={otherReason.checked}
              handleChange={(e) => {
                setOtherReason({ ...otherReason, checked: e.target.checked });
              }}
              dataTestId="Withdraw-otherReason"
            />
            <OtherReasonTextField
              disabled={!otherReason.checked}
              value={otherReason.value}
              onChange={(e) => {
                setOtherReason({ ...otherReason, value: e.target.value });
              }}
              onBlur={(e) => {
                setOtherReason({ ...otherReason, value: e.target.value.trim() });
              }}
              placeholder={t('withdraw.form.placeholder')}
              inputProps={{
                ...register('otherReason', {
                  validate: {
                    ...utf8mb4ValidationRule.validate,
                    selectedRequired: (value) =>
                      otherReason.checked && value.trim() === ''
                        ? t('validation.requiredSelectInput')
                        : null,
                  },
                }),
                'data-testid': 'Withdraw-otherReason-TextField',
              }}
            />
            {!!errors.otherReason && (
              <InputValidationErrorBox
                message={errors.otherReason.message}
                dataTestId="otherReason"
                position="left"
              />
            )}

            <NoRequiredGmoResearchInfo>
              <Checkbox
                name="noRequiredGmoResearchInfo"
                label={noRequiredGmoResearchInfo?.text}
                checked={noRequiredGmoResearchInfo?.checked}
                handleChange={(e) => {
                  setNoRequiredGmoResearchInfo({
                    ...noRequiredGmoResearchInfo,
                    checked: e.target.checked,
                  });
                }}
                dataTestId="Withdraw-noRequiredGmoResearchInfo"
              />
            </NoRequiredGmoResearchInfo>

            <Opinion>
              <TextFieldWithLabel
                label={t('withdraw.form.opinionTitle')}
                name="opinion"
                value={opinion}
                handleChange={(e) => setOpinion(e.target.value)}
                handleBlur={(e) => setOpinion(e.target.value.trim())}
                rows={5}
                width="480px"
                labelColor="secondary"
                inputProps={{
                  ...register('opinion', utf8mb4ValidationRule),
                }}
                dataTestId="Withdraw-TextFieldWithLabel-opinion"
              />
            </Opinion>
            {!!errors.opinion && (
              <InputValidationErrorBox
                message={errors.opinion.message}
                dataTestId="opinion"
                position="left"
              />
            )}
            <SubmitButton
              type="submit"
              color="primary"
              variant="contained"
              data-testid="Withdraw-SubmitButton"
            >
              {t('withdraw.form.submit')}
            </SubmitButton>
          </StyledForm>
        </Wrapper>
      )}
    </>
  );
}

export default Withdraw;
